import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Navbar } from '../../components/Navbar/Navbar';
import { useMediaDevices } from '../../hooks/useMediaDevices';
import { useToast } from '../../components/common/Toast';
import CameraPreview from './CameraPreview';
import DeviceControls from './DeviceControls';
import InterviewDetailsCard from './InterviewDetailsCard';
import LanguageSelector from './LanguageSelector';
import ResumeSelector from './ResumeSelector';
import ActionButtons from './ActionButtons';
import NotEnoughMinutesModal from './NotEnoughMinutesModal';
import { InterviewDetails, InterviewPreviewParams } from '../../types/interviews';
import { ResumeProtocol } from '../../types/resume';
import subscriptionService from '../../services/subscriptionService';
import careerService from '../../services/careerService';
import { getAudioStreamer } from '../../modules/interview/contexts/InterviewContext';
import configService, { Language } from '../../services/configService';
import authService from '../../services/authService';

// Add new component for device error alerts
import DeviceErrorAlert from './DeviceErrorAlert';
import interviewService from '../../services/interviewService';
import ConfirmationModal from '../common/ConfirmationModal';
import { useWeekendDetection } from '../../hooks/useWeekendDetection';

interface InterviewPreviewContainerProps {
  userDetails: {
    id: string;
    name: string;
    profilePicture?: string;
  };
}

const InterviewPreviewContainer: React.FC<InterviewPreviewContainerProps> = ({ userDetails }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const interviewId = params.id;
  const { showToast } = useToast();
  const { isWeekend } = useWeekendDetection();

  // Get interview details from route state or use defaults
  const interviewDetails: InterviewPreviewParams = location.state?.interviewDetails;
  
  // DEBUG: Log the interview details received in preview page
  console.log('[InterviewPreviewContainer] RECEIVED interview details:', JSON.stringify(interviewDetails, null, 2));
  console.log('[InterviewPreviewContainer] voicePack value:', interviewDetails?.voicePack);
  console.log('[InterviewPreviewContainer] characterGender value:', interviewDetails?.characterGender);
  console.log('[InterviewPreviewContainer] interviewPrompt value:', interviewDetails?.interviewPrompt);

  // Language and resume state
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    interviewDetails.language || 'English'
  );
  const [selectedLanguageId, setSelectedLanguageId] = useState<string>('');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [resumeList, setResumeList] = useState<ResumeProtocol[]>([]);
  const [selectedResume, setSelectedResume] = useState<ResumeProtocol | null>(null);
  const [isLoadingResumes, setIsLoadingResumes] = useState<boolean>(true);

  // Device dropdown state
  const [isDeviceDropdownOpen, setIsDeviceDropdownOpen] = useState<boolean>(false);

  // Loading state
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Mobile view state
  const [, setIsMobileView] = useState<boolean>(false);

  // Subscription related state
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState<boolean>(true);
  const [showNotEnoughMinutesModal, setShowNotEnoughMinutesModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Add device error state
  const [deviceError, setDeviceError] = useState<{
    hasError: boolean;
    message: string;
    isBlocking: boolean;
  }>({
    hasError: false,
    message: '',
    isBlocking: false
  });

  // Add debounce ref for error handling
  const errorDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add component stability monitoring
  useEffect(() => {
    const mountTime = Date.now();
    console.log(`[InterviewPreviewContainer] Component mounted at ${new Date(mountTime).toISOString()}`);

    // Store all mounts to detect potential issues
    if (!window.mocktrailMountCycles) {
      window.mocktrailMountCycles = [];
    }

    // Add this mount
    window.mocktrailMountCycles.push({
      component: 'InterviewPreviewContainer',
      mountTime
    });

    // Check for rapid remounting
    const recentMounts = window.mocktrailMountCycles
      .filter(cycle =>
        cycle.component === 'InterviewPreviewContainer' &&
        mountTime - cycle.mountTime < 5000
      );

    if (recentMounts.length > 3) {
      console.warn(`[InterviewPreviewContainer] Detected ${recentMounts.length} rapid mount cycles. Possible render loop!`);
    }

    return () => {
      console.log(`[InterviewPreviewContainer] Component unmounted after ${((Date.now() - mountTime) / 1000).toFixed(2)}s`);

      // Remove this mount from tracking
      if (window.mocktrailMountCycles) {
        window.mocktrailMountCycles = window.mocktrailMountCycles.filter(
          cycle => cycle.mountTime !== mountTime
        );
      }
    };
  }, []);

  // Memoize the error handler to avoid recreation on each render
  const handleMediaError = useCallback((error: Error) => {
    console.error('Media devices error:', error);

    // Clear any existing timeout
    if (errorDebounceTimeoutRef.current) {
      clearTimeout(errorDebounceTimeoutRef.current);
    }

    // Set a new timeout to debounce the error display
    errorDebounceTimeoutRef.current = setTimeout(() => {
      // Handle different types of errors
      if (error.name === 'NotFoundError') {
        setDeviceError({
          hasError: true,
          message: 'No camera or microphone found. Please connect a device and try again.',
          isBlocking: true
        });
      } else if (error.name === 'NotAllowedError') {
        setDeviceError({
          hasError: true,
          message: 'Permission to use camera or microphone was denied. Please enable permissions in your browser settings.',
          isBlocking: true
        });
      } else {
        setDeviceError({
          hasError: true,
          message: `Error accessing media devices: ${error.message || error.name}`,
          isBlocking: true
        });
      }
    }, 500); // 500ms debounce
  }, []);

  // Use the media devices hook with memoized error handler
  const mediaDevices = useMediaDevices({
    initialCameraEnabled: false, // We don't want to start with camera enabled, we want to save money and let user toggle it on
    initialMicEnabled: true,
    onError: handleMediaError
  });

  const {
    videoRef,
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    isCameraEnabled,
    isMicEnabled,
    hasCameraPermission,
    hasMicPermission,
    toggleCamera,
    toggleMicrophone,
    handleDeviceChange,
    cleanupMediaTracks,
    requestMediaAccess,
    stream
  } = mediaDevices;

  // Add new state variables for device toggling status
  const [isTogglingCamera, setIsTogglingCamera] = useState<boolean>(false);
  const [isTogglingMic, setIsTogglingMic] = useState<boolean>(false);

  // Add a new state variable for cancel loading:
  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  // Add a new state to track if stream is initialized
  const [isStreamInitialized, setIsStreamInitialized] = useState<boolean>(false);

  // Add state to track retry attempts
  const [mediaRetryAttempts, setMediaRetryAttempts] = useState<number>(0);
  const MAX_RETRY_ATTEMPTS = 3;

  // Update error states whenever permissions change
  useEffect(() => {
    // Clear any existing device errors if we have both permissions
    if (hasMicPermission === true) {
      setDeviceError(prev => ({
        ...prev,
        hasError: false,
        isBlocking: false
      }));
    }
    // Check for mic permission denied (required)
    else if (hasMicPermission === false) {
      setDeviceError({
        hasError: true,
        message: 'Microphone access is required for the interview. Please enable it in your browser settings.',
        isBlocking: true
      });
    }

  }, [hasCameraPermission, hasMicPermission]);

  // Simpler approach to camera initialization with retry logic
  useEffect(() => {
    // Don't try to initialize again if we're already initialized
    if (isStreamInitialized) return;

    // Don't retry beyond max attempts
    if (mediaRetryAttempts >= MAX_RETRY_ATTEMPTS) {
      console.log('[InitCamera] Maximum retry attempts reached');

      // Check what devices are available
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const hasAudioDevices = devices.some(device => device.kind === 'audioinput');
          const hasVideoDevices = devices.some(device => device.kind === 'videoinput');

          if (!hasAudioDevices && !hasVideoDevices) {
            setDeviceError({
              hasError: true,
              message: 'No audio or video devices found. Please connect a microphone and try again.',
              isBlocking: true
            });
          } else if (!hasAudioDevices) {
            setDeviceError({
              hasError: true,
              message: 'No microphone found. A microphone is required for the interview.',
              isBlocking: true
            });
          } else {
            // Fallback error - something else went wrong
            setDeviceError({
              hasError: true,
              message: 'Failed to initialize media devices after multiple attempts.',
              isBlocking: true
            });
          }
        })
        .catch(error => {
          console.error('[InitCamera] Error checking devices:', error);
          setDeviceError({
            hasError: true,
            message: 'Could not detect available media devices. Please ensure you have a microphone connected.',
            isBlocking: true
          });
        });

      return;
    }

    console.log(`[InitCamera] Initializing camera (attempt ${mediaRetryAttempts + 1}/${MAX_RETRY_ATTEMPTS})`);

    const initCamera = async () => {
      try {
        // First clean up any existing media tracks
        await cleanupMediaTracks();

        // Request camera and microphone access with a simple approach
        await requestMediaAccess();

        // If successful, mark as initialized
        setIsStreamInitialized(true);

        // Clear any device errors
        setDeviceError({
          hasError: false,
          message: '',
          isBlocking: false
        });

        // The stream is handled by the hooks and CameraPreview component
        console.log('[InitCamera] Camera initialized successfully');
      } catch (error) {
        console.error('[InitCamera] Error initializing camera:', error);

        // Increment retry counter and try again with a delay
        setMediaRetryAttempts(prev => prev + 1);
      }
    };

    // Begin initialization
    initCamera();

    // Cleanup on unmount
    return () => {
      if (!isStreamInitialized) {
        console.log('[InitCamera] Component unmounted before initialization completed');
      }
    };
  }, [cleanupMediaTracks, requestMediaAccess, isStreamInitialized, mediaRetryAttempts]);

  // Simplified stream watcher to ensure video is playing
  useEffect(() => {
    if (!stream || !videoRef.current) return;

    // Stream exists and we have a video element, ensure it's connected
    if (videoRef.current.srcObject !== stream) {
      console.log('[StreamWatcher] Connecting stream to video element');
      videoRef.current.srcObject = stream;
    }

    // Ensure the video is marked as initialized
    if (!isStreamInitialized && stream.active) {
      console.log('[StreamWatcher] Active stream detected, marking as initialized');
      setIsStreamInitialized(true);
    }
  }, [stream, videoRef, isStreamInitialized]);

  // Toggle device dropdown
  const toggleDeviceDropdown = () => {
    setIsDeviceDropdownOpen(prev => !prev);
  };

  // Enhanced toggle handlers for better UX
  const handleToggleCamera = () => {
    setIsTogglingCamera(true);
    // Slight delay to show the loading state
    setTimeout(() => {
      toggleCamera();
      // Add a small delay for user feedback
      setTimeout(() => {
        setIsTogglingCamera(false);
      }, 300);
    }, 100);
  };

  const handleToggleMicrophone = () => {
    // // Don't allow turning off the microphone if it's required
    // if (isMicEnabled) {
    //   setDeviceError({
    //     hasError: true,
    //     message: 'Microphone is required for the interview and cannot be disabled.',
    //     isBlocking: false
    //   });
    //   return;
    // }

    setIsTogglingMic(true);
    // Slight delay to show the loading state
    setTimeout(() => {
      toggleMicrophone();
      // Add a small delay for user feedback
      setTimeout(() => {
        setIsTogglingMic(false);
      }, 300);
    }, 100);
  };

  // Handle language selection change
  const handleLanguageChange = (languageId: string) => {
    setSelectedLanguageId(languageId);

    // Find and update the language name to keep both states in sync
    const language = languages.find((lang: Language) => lang._id === languageId);
    if (language) {
      setSelectedLanguage(language.name);
    }
  };

  // Handle interview start
  const handleStartInterview = async (allowContinue = false) => {
    console.log('[InterviewPreviewContainer] Interview details:', interviewDetails);
    // Check if microphone is enabled and available - this is required
    if (!isMicEnabled || hasMicPermission !== true) {
      setDeviceError({
        hasError: true,
        message: 'A microphone is required for the interview. Please connect one and grant permission.',
        isBlocking: true
      });
      return;
    }

    const remainingMinutes = subscriptionInfo?.minutesLeft || 0;
    // Use isWeekend from useWeekendDetection to skip minutes check
    if (!isWeekend && remainingMinutes <= 0) {
      setErrorMessage(`You have 0 minutes left to give this interview. Please get some more minutes to continue.`);
      setShowNotEnoughMinutesModal(true);
      return;
    }
    
    // Check if user has enough minutes to take the interview
    // NOTE: We're no longer handling interview duration as it will be dynamic so no such warning.
    // // Check if user has enough minutes to take the interview
    // const requiredMinutes = interviewDetails.duration || 30;
    // const remainingMinutes = subscriptionInfo?.minutesLeft || 0;

    // console.log('[InterviewPreviewContainer] Required minutes:', requiredMinutes);
    // console.log('[InterviewPreviewContainer] Remaining minutes:', remainingMinutes);
    // console.log('[InterviewPreviewContainer] Allow continue:', allowContinue);
    // if (remainingMinutes < requiredMinutes && !allowContinue) {
    //   // Not enough minutes, show error modal
    //   if (remainingMinutes > 0) {
    //     setErrorMessage(`You need ${requiredMinutes} minutes to take this interview, but you can only interview for ${remainingMinutes} minutes. Your interview will be cut short once minutes are up.`);
    //     setShowNotEnoughMinutesModal(true);
    //   } else {
    //     setErrorMessage(`You have no minutes left. Please subscribe to continue`);
    //     setShowNotEnoughMinutesModal(true);
    //   }
    //   return;
    // }
    
    // Resume audio context to allow playback
    try {
      getAudioStreamer().resume();
    } catch (e) {
      console.warn('Failed to resume audio context:', e);
    }
    if (!interviewId) {
      showToast ? showToast('Interview ID not found', 'error') : alert('Interview ID not found');
      return;
    }

    // Always refresh authentication token before starting interview
    console.log('[InterviewPreviewContainer] Refreshing authentication token before interview');
    try {
      setIsLoading(true);

      const refreshResult = await authService.refreshToken();
      console.log('[InterviewPreviewContainer] Token refresh successful:', refreshResult);

      if (!refreshResult.access_token && !refreshResult.token) {
        throw new Error('Token refresh did not return a valid token');
      }

      console.log('[InterviewPreviewContainer] Authentication token refreshed successfully');
    } catch (refreshError) {
      console.error('[InterviewPreviewContainer] Token refresh failed:', refreshError);
      setIsLoading(false);
      showToast ? showToast('Failed to refresh authentication. Please log in again.', 'error') : alert('Session expired');
      return;
    } finally {
      setIsLoading(false);
    }

    setIsLoading(true);

    try {
      // Store device selections in sessionStorage BEFORE any cleanup or timeout
      // This ensures the device IDs are preserved even if the stream is cleaned up
      try {
        console.log("Saving media device selections to session storage for interview page");
        sessionStorage.setItem('selectedVideoDevice', selectedVideoDevice);
        sessionStorage.setItem('selectedAudioDevice', selectedAudioDevice);
        sessionStorage.setItem('hasCameraPermission', (hasCameraPermission?.toString() && isCameraEnabled?.toString()) || 'false');
        sessionStorage.setItem('hasMicPermission', (hasMicPermission?.toString() && isMicEnabled?.toString()) || 'false');

        // Also save the device labels if available, which can help with device identification
        if (videoDevices.length > 0 && selectedVideoDevice) {
          const selectedVideoDeviceObj = videoDevices.find(device => device.deviceId === selectedVideoDevice);
          if (selectedVideoDeviceObj && selectedVideoDeviceObj?.label) {
            sessionStorage.setItem('selectedVideoDeviceLabel', selectedVideoDeviceObj?.label || '');
          }
        }

        if (audioDevices.length > 0 && selectedAudioDevice) {
          const selectedAudioDeviceObj = audioDevices.find(device => device.deviceId === selectedAudioDevice);
          if (selectedAudioDeviceObj && selectedAudioDeviceObj?.label) {
            sessionStorage.setItem('selectedAudioDeviceLabel', selectedAudioDeviceObj?.label || '');
          }
        }
      } catch (error) {
        console.error("Error storing media selections:", error);
        // Non-critical error, continue with navigation
      }
      setIsLoading(true);
      let userInterviewId = '';
      // Check if it is a new interview(from dashboard) or a scheduled interview(from calendar)
      if (interviewDetails.newInterview) {
        // Check if this is a custom interview
        if (interviewDetails.isCustom && interviewDetails.id) {
          // Create a new UserInterview session from custom interview template
          console.log('[InterviewPreviewContainer] Creating custom interview session from template:', interviewDetails.id);
          const newCustomSession = await interviewService.startCustomInterviewSession(
            interviewDetails.id,
            new Date(),
            selectedLanguageId || undefined
          );
          console.log('[InterviewPreviewContainer] New custom interview session created:', newCustomSession);
          userInterviewId = newCustomSession?._id || '';
        } else {
          // Create a regular new interview
          const newInterviewCreated = await interviewService.scheduleInterview(
            interviewDetails.id || '', 
            new Date(), 
            selectedLanguageId || undefined, 
            interviewDetails.series?._id || undefined
          );
          console.log('[InterviewPreviewContainer] New interview created:', newInterviewCreated);
          userInterviewId = newInterviewCreated?._id || '';
        }
      } else {
        // Otherwise, use the scheduled interview id
        userInterviewId = interviewDetails.scheduledInterviewId || '';
      }

      // Update the status of the interview to in_progress
      await interviewService.joinInterview(userInterviewId, selectedLanguageId || undefined);

      console.log('[InterviewPreviewContainer] Navigating to interview page with userInterviewId:', userInterviewId);
      
      // DEBUG: Log what we're about to send to interview page
      const interviewDetailsToSend = {
        ...interviewDetails,
        companyLogo: interviewDetails.companyLogo,
        status: 'in_progress',
        language: selectedLanguage,
        languageId: selectedLanguageId,
        languageName: selectedLanguage,
        selectedResume: selectedResume
      } as InterviewDetails;
      
      console.log('[InterviewPreviewContainer] SENDING interview details to InterviewPage:', JSON.stringify(interviewDetailsToSend, null, 2));
      console.log('[InterviewPreviewContainer] SENDING voicePack:', interviewDetailsToSend?.voicePack);
      console.log('[InterviewPreviewContainer] SENDING characterGender:', interviewDetailsToSend?.characterGender);
      console.log('[InterviewPreviewContainer] SENDING interviewPrompt:', interviewDetailsToSend?.interviewPrompt);
      
      setIsLoading(false);
      navigate(`/interview/${userInterviewId}`, {
        state: {
          userInterviewId: userInterviewId, // This is the userInterviewId which means user's scheduled interview id
          interviewDetails: interviewDetailsToSend
        }
      });
    } catch (error) {
      console.error('Error joining interview:', error);
      setIsLoading(false);
      showToast ? showToast('Failed to start interview. Please try again.', 'error') : alert('Failed to start interview');
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Clean up media before navigating away using the cancel loading state
    setIsCancelling(true);
    console.log("Cleaning up media tracks before navigation...");

    cleanupMediaTracks()
      .then(() => {
        console.log("Media tracks successfully cleaned up, now navigating away");
        // Verify camera is released by checking if it can be accessed
        try {
          navigator.mediaDevices.getUserMedia({ video: true, audio: false })
            .then(verifyStream => {
              // Successfully got camera, it was released - stop it immediately
              verifyStream.getTracks().forEach(track => track.stop());
              console.log("Camera successfully released (verified)");
            })
            .catch(e => {
              console.log("Camera verification check failed:", e);
            })
            .finally(() => {
              // Navigate away regardless of verification result
              setTimeout(() => {
                setIsCancelling(false);
                navigate(-1);
              }, 300);
            });
        } catch (e) {
          // Navigate away even if verification fails
          console.error("Error during cleanup verification:", e);
          setTimeout(() => {
            setIsCancelling(false);
            navigate(-1);
          }, 300);
        }
      })
      .catch(error => {
        console.error("Error during media cleanup:", error);
        // Navigate away even if cleanup fails
        setTimeout(() => {
          setIsCancelling(false);
          navigate(-1);
        }, 300);
      });
  };

  // Add useEffect to detect mobile view
  useEffect(() => {
    const checkMobileView = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);
    };

    // Initial check
    checkMobileView();

    // Add listener
    window.addEventListener('resize', checkMobileView);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  // Add custom scrollbar styles for mobile
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media (max-width: 768px) {
        .scrollbar-hide-mobile {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide-mobile::-webkit-scrollbar {
          display: none;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch subscription info
  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      try {
        const subscriptionData = await subscriptionService.getSubscriptionInfo();
        setSubscriptionInfo(subscriptionData);
      } catch (error) {
        console.error('Error fetching subscription info:', error);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchSubscriptionInfo();
  }, []);

  // Fetch user's resumes
  useEffect(() => {
    const fetchUserResumes = async () => {
      setIsLoadingResumes(true);
      try {
        // Clear cache to ensure fresh data
        careerService.clearResumeCache();
        
        // Fetch resumes with retry logic built-in
        const resumes = await careerService.getResumes();
        
        // If we have resumes, set them and select the first one
        if (resumes.length > 0) {
          console.log('InterviewPreviewContainer: Found', resumes.length, 'resumes');
          setResumeList(resumes);
          setSelectedResume(resumes[0]);
        } else {
          // Otherwise use a default resume name
          console.log('InterviewPreviewContainer: No resumes found');
          setResumeList([]);
          setSelectedResume(null);
        }
      } catch (error) {
        console.error('Error fetching user resumes:', error);
        // Set default in case of error
        setResumeList([]);
        setSelectedResume(null);
      } finally {
        setIsLoadingResumes(false);
      }
    };

    fetchUserResumes();
  }, []);

  // Add handler for refreshing resumes
  const handleRefreshResumes = async () => {
    setIsLoadingResumes(true);
    try {
      // Clear cache and force refresh
      careerService.clearResumeCache();
      const resumes = await careerService.getResumes(true); // Force refresh
      
      if (resumes.length > 0) {
        console.log('InterviewPreviewContainer: Refreshed and found', resumes.length, 'resumes');
        setResumeList(resumes);
        setSelectedResume(resumes[0]);
        showToast('Resumes refreshed successfully', 'success');
      } else {
        console.log('InterviewPreviewContainer: No resumes found after refresh');
        setResumeList([]);
        setSelectedResume(null);
      }
    } catch (error) {
      console.error('Error refreshing resumes:', error);
      setResumeList([]);
      setSelectedResume(null);
      showToast('Failed to refresh resumes. Please try again.', 'error');
    } finally {
      setIsLoadingResumes(false);
    }
  };

  // Handle navigate to subscription or home
  const handleGoToSubscription = () => {
    setShowNotEnoughMinutesModal(false);
    navigate('/subscriptions');
  };

  const handleGoToHome = () => {
    setShowNotEnoughMinutesModal(false);
    navigate('/');
  };

  // Determine if start button should be disabled
  const isStartDisabled = () => {
    // Always disable if there's a blocking error
    if (deviceError.isBlocking) return true;

    // Disable if microphone permission is not granted or mic is not enabled
    if (hasMicPermission !== true || isMicEnabled !== true) return true;

    return false;
  };

  // Add cleanup for debounce timeout
  useEffect(() => {
    return () => {
      if (errorDebounceTimeoutRef.current) {
        clearTimeout(errorDebounceTimeoutRef.current);
      }
    };
  }, []);

  // Add useEffect to fetch languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languagesData = await configService.getLanguages();
        setLanguages(languagesData);

        // Find the language that matches the selected name and set its ID
        const language = languagesData.find(lang => lang.name === selectedLanguage);
        if (language) {
          setSelectedLanguageId(language._id);
          // Ensure we use the exact language name from the server
          setSelectedLanguage(language.name);
        } else if (languagesData.length > 0) {
          // If no match found, use the first language
          setSelectedLanguage(languagesData[0].name);
          setSelectedLanguageId(languagesData[0]._id);
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
      }
    };

    fetchLanguages();
  }, []); // Empty dependency array to run only once

  return (
    <div className="fixed inset-0 bg-background-light dark:bg-background-dark z-50 flex flex-col overflow-auto md:overflow-auto scrollbar-hide-mobile">
      {/* Animated Gradient Background */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-light/5 via-background-light to-secondary-light/5 dark:from-primary-dark/10 dark:via-background-dark dark:to-secondary-dark/10 animate-gradient-slow"></div>

        {/* Particle background effect */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary-light/20 dark:bg-primary-dark/20 rounded-full filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-secondary-light/20 dark:bg-secondary-dark/20 rounded-full filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-tertiary-light/20 dark:bg-tertiary-dark/20 rounded-full filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>
      </div>

      {/* Fixed Navbar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar
          showUserActions={false}
          onClose={handleCancel}
          subscriptionInfo={subscriptionInfo}
          isLoadingSubscription={isLoadingSubscription}
          showLogoText={true}
        />
      </div>

      {/* Device Error Modal - Commented out for now */}
      {/* <DeviceErrorAlert
        message={deviceError.message}
        isBlocking={deviceError.isBlocking}
        isOpen={deviceError.hasError}
        onDismiss={() => {
          setDeviceError(prev => ({ ...prev, hasError: false }));
          navigate(-1);
        }}
        onRetry={() => {
          setMediaRetryAttempts(0);
          setIsStreamInitialized(false);
          setDeviceError({
            hasError: false,
            message: '',
            isBlocking: false
          });
        }}
      /> */}

      {/* Main content wrapper */}
      <div className="flex flex-col h-full md:h-screen pt-2 md:pt-16 md:justify-center">
        {/* Main content */}
        <div className="relative z-10 max-w-7xl w-full flex flex-col md:flex-row gap-2 px-4 md:px-0 mx-auto">
          {/* Left side - Camera preview */}
          <div className="w-full md:w-3/5 h-[28vh] md:h-[60vh] mt-24 md:mt-0 flex items-center justify-center mb-2 md:mb-0">
            <CameraPreview
              videoRef={videoRef}
              isCameraEnabled={isCameraEnabled}
              profilePicture={userDetails.profilePicture}
              stream={stream}
              errorState={deviceError.hasError}
            >
              <DeviceControls
                isCameraEnabled={isCameraEnabled}
                isMicEnabled={isMicEnabled}
                toggleCamera={handleToggleCamera}
                toggleMicrophone={handleToggleMicrophone}
                isDeviceDropdownOpen={isDeviceDropdownOpen}
                toggleDeviceDropdown={toggleDeviceDropdown}
                videoDevices={videoDevices}
                audioDevices={audioDevices}
                selectedVideoDevice={selectedVideoDevice}
                selectedAudioDevice={selectedAudioDevice}
                handleDeviceChange={handleDeviceChange}
                isTogglingCamera={isTogglingCamera}
                isTogglingMic={isTogglingMic}
                disableMicToggle={false}
              />
            </CameraPreview>
          </div>

          {/* Right side - Interview details */}
          <div className="w-full md:w-2/5 h-auto md:h-[60vh] rounded-xl border border-border-light dark:border-border-dark bg-surface-light/90 dark:bg-surface-dark/90 backdrop-blur-md overflow-hidden animate-navbar-item-fade-in">
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-y-visible md:overflow-y-auto scrollbar-hide-mobile px-4 pt-4 md:px-6 md:pt-6 pb-2">
                <h1 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
                  Interview Preview
                </h1>
                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4 text-sm">
                  Prepare yourself and check your settings before starting
                </p>

                <InterviewDetailsCard interviewDetails={interviewDetails} />

                <div className="space-y-4 mb-2 md:mb-4">
                  <LanguageSelector
                    selectedLanguage={selectedLanguage}
                    onChange={handleLanguageChange}
                  />

                  <ResumeSelector
                    resumeList={resumeList}
                    selectedResume={selectedResume}
                    onChange={setSelectedResume}
                    onRefresh={handleRefreshResumes}
                    shouldShowRefresh={resumeList.length == 0}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <ActionButtons
          isLoading={isLoading}
          isCancelling={isCancelling}
          isStartDisabled={isStartDisabled()}
          onStart={() => handleStartInterview(false)}
          onCancel={handleCancel}
        />
      </div>

      <ConfirmationModal
        isOpen={showNotEnoughMinutesModal}
        title='Not Enough Minutes'
        message={errorMessage}
        confirmVariant='secondary'
        confirmText={'Buy More Minutes'}
        cancelText={'Back to Dashboard'}
        onConfirm={handleGoToSubscription}
        onCancel={handleGoToHome}
      />
      {/* <ConfirmationModal
        isOpen={showNotEnoughMinutesModal}
        title='Not Enough Minutes'
        message={errorMessage}
        confirmVariant='secondary'
        confirmText={subscriptionInfo?.minutesLeft > 0 ? 'Continue Anyway' : !!subscriptionInfo?.planType ? 'Buy More Minutes' : 'Subscribe Now'}
        cancelText={subscriptionInfo?.minutesLeft > 0 ? !!subscriptionInfo?.planType ? 'Buy More Minutes' : 'Back to Dashboard' : 'Back to Dashboard'}
        onConfirm={subscriptionInfo?.minutesLeft > 0 ? () => {
          setShowNotEnoughMinutesModal(false);
          handleStartInterview(true)
        } : !!subscriptionInfo?.planType ? handleGoToSubscription : handleGoToHome}
        onCancel={subscriptionInfo?.minutesLeft > 0 ? !!subscriptionInfo?.planType ? handleGoToSubscription : handleGoToHome : handleGoToHome}
      /> */}
    </div>
  );
};

export default InterviewPreviewContainer; 